import { useEffect, useState } from 'react'

import { autoUpdate, flip, offset, shift, useFloating, UseFloatingReturn } from '@floating-ui/react'

/**
 * Find the closest scrollable ancestor element (like a modal container)
 */
function findScrollableAncestor(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null

  let current = element.parentElement
  while (current) {
    const { overflow, overflowY } = window.getComputedStyle(current)
    const isScrollable = /(auto|scroll)/.test(overflow + overflowY)

    if (isScrollable && current.scrollHeight > current.clientHeight) {
      return current
    }

    current = current.parentElement
  }

  return null
}

/**
 * A custom hook that manages dynamic positioning and visibility of a dropdown menu relative to a control element.
 *
 * This hook provides automatic boundary detection, scroll-based visibility management.
 * It's designed to work within scrollable containers (like modals) and will automatically
 * flip the menu up/down and close the menu when the control element scrolls out of view.
 *
 * @param params - Configuration object for the menu control
 * @param params.controlRef - React ref to the control element (e.g., button, input) that triggers the menu
 * @param params.isOpen - Whether the menu is currently open (defaults to true)
 * @param params.onClose - Callback function to close the menu when it scrolls out of view
 *
 * @example
 * ```tsx
 * const inputRef = useRef<HTMLInputElement>(null)
 * const { refs, floatingStyles, isPositioned } = useDynamicMenuControl({
 *   controlRef: inputRef,
 *   isOpen: isMenuOpen,
 *   onClose: () => setIsMenuOpen(false)
 * })
 * ```
 */
export const useDynamicMenuControl = ({
  controlRef,
  isOpen = true,
  onClose,
}: {
  controlRef: React.RefObject<HTMLElement>
  isOpen?: boolean
  onClose: () => void
}): {
  refs: UseFloatingReturn['refs']
  floatingStyles: UseFloatingReturn['floatingStyles']
} => {
  const [boundary, setBoundary] = useState<HTMLElement | undefined>(undefined)
  const [isPositioned, setIsPositioned] = useState(false)

  // Detect scrollable ancestor (modal container) when menu opens
  useEffect(() => {
    if (isOpen && controlRef.current) {
      const scrollableAncestor = findScrollableAncestor(controlRef.current)
      setBoundary(scrollableAncestor || undefined)
    }
  }, [controlRef, isOpen])

  // Close dropdown when control element scrolls out of view
  useEffect(() => {
    if (!isOpen || !controlRef.current) return

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries
        if (!entry.isIntersecting) {
          onClose()
        }
      },
      {
        root: boundary || null, // Use modal container as root if available
        threshold: 0,
      }
    )

    observer.observe(controlRef.current)

    return () => {
      observer.disconnect()
    }
  }, [isOpen, onClose, boundary, controlRef])

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({
        fallbackAxisSideDirection: 'start',
        padding: 8,
        boundary: boundary,
      }),
      shift({
        padding: 8,
        boundary: boundary,
      }),
    ],
  })

  useEffect(() => {
    if (controlRef?.current) {
      refs.setReference(controlRef.current)
    }
  }, [controlRef, refs])

  // Mark as positioned after floating styles are calculated
  useEffect(() => {
    if (isOpen && floatingStyles.position && floatingStyles.transform) {
      requestAnimationFrame(() => {
        setIsPositioned(true)
      })
    }
  }, [isOpen, floatingStyles])

  const combinedFloatingStyles: React.CSSProperties = {
    ...floatingStyles,
    visibility: isPositioned ? 'visible' : 'hidden',
    zIndex: 99999,
  }

  return { refs, floatingStyles: combinedFloatingStyles }
}
